import React, { useState } from 'react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { useAuth } from '../../hooks/useAuth';
import { 
  Loader2, 
  AlertCircle,
  Check,
  RefreshCw,
  Users,
  UserPlus,
  UserMinus,
  Download,
  Database
} from 'lucide-react';

const FixMissingProfilesPage: React.FC = () => {
  const { supabase } = useSupabase();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleFixProfiles = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    setResults(null);

    try {
      // First try using the Edge Function
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.access_token) {
          throw new Error('Sessão expirada. Faça login novamente.');
        }

        const { data, error } = await supabase.functions.invoke('fix-missing-profiles', {
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        });

        if (error) {
          throw error;
        }

        setResults(data);
        setSuccess(`Processo concluído. ${data.profiles_created} perfis criados, ${data.profiles_failed} falhas.`);
      } catch (edgeFunctionError) {
        console.error('Edge function error:', edgeFunctionError);
        
        // Fallback to RPC function
        const { data: rpcData, error: rpcError } = await supabase.rpc('admin_fix_missing_profiles');
        
        if (rpcError) {
          throw rpcError;
        }
        
        setResults({
          profiles_created: rpcData,
          method: 'rpc'
        });
        setSuccess(`Processo concluído via RPC. ${rpcData} perfis criados.`);
      }
    } catch (err: any) {
      console.error('Error fixing profiles:', err);
      setError(err.message || 'Erro ao corrigir perfis');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <header className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Corrigir Perfis Ausentes</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Ferramenta administrativa para criar perfis para usuários que não os possuem
        </p>
      </header>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          <p>{success}</p>
        </div>
      )}

      <div className="card p-6 mb-6">
        <div className="flex items-center gap-3 text-primary-600 dark:text-primary-400 mb-4">
          <Database className="w-8 h-8" />
          <h2 className="text-xl font-bold">Sincronização de Perfis</h2>
        </div>

        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Esta ferramenta verifica todos os usuários no sistema e cria perfis para aqueles que não possuem.
          Isso pode acontecer quando o trigger de criação de perfil falha durante o registro do usuário.
        </p>

        <button
          onClick={handleFixProfiles}
          className="btn-primary w-full"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Processando...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Corrigir Perfis Ausentes
            </>
          )}
        </button>
      </div>

      {results && (
        <div className="card p-6">
          <h2 className="text-lg font-bold mb-4">Resultados</h2>
          
          {results.method === 'rpc' ? (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-blue-600 dark:text-blue-400 font-medium">
                Processo executado via RPC
              </p>
              <p className="mt-2">
                {results.profiles_created} perfis foram criados com sucesso.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex flex-col items-center">
                  <Users className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                  <p className="text-sm text-gray-500">Total de Usuários</p>
                  <p className="text-2xl font-bold">{results.total_users}</p>
                </div>
                
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg flex flex-col items-center">
                  <UserPlus className="w-8 h-8 text-green-600 dark:text-green-400 mb-2" />
                  <p className="text-sm text-gray-500">Perfis Criados</p>
                  <p className="text-2xl font-bold">{results.profiles_created}</p>
                </div>
                
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg flex flex-col items-center">
                  <UserMinus className="w-8 h-8 text-red-600 dark:text-red-400 mb-2" />
                  <p className="text-sm text-gray-500">Falhas</p>
                  <p className="text-2xl font-bold">{results.profiles_failed}</p>
                </div>
              </div>
              
              {results.results && results.results.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2">Detalhes</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-dark-light">
                          <th className="text-left p-2">Email</th>
                          <th className="text-left p-2">Status</th>
                          <th className="text-left p-2">Erro</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y dark:divide-dark-border">
                        {results.results.map((result: any, index: number) => (
                          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-dark-light">
                            <td className="p-2">{result.email}</td>
                            <td className="p-2">
                              {result.success ? (
                                <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                                  <Check className="w-4 h-4" />
                                  Sucesso
                                </span>
                              ) : (
                                <span className="text-red-600 dark:text-red-400 flex items-center gap-1">
                                  <AlertCircle className="w-4 h-4" />
                                  Falha
                                </span>
                              )}
                            </td>
                            <td className="p-2 text-sm text-gray-500">
                              {result.error || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FixMissingProfilesPage;